/**
 * @Module: Preferences Context
 * @Status: Ready
 * @Summary: React context for UserPreferences with Firestore sync + localStorage fallback
 * @Description: PreferencesProvider wraps the app and exposes usePreferences().
 *   On mount, loads from Firestore (if signed in) or localStorage. Updates write
 *   to both. Mirrors KitchenContext pattern for consistency.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import type { UserPreferences } from '../types/preferences';
import { DEFAULT_PREFERENCES } from '../types/preferences';
import { useAuth } from './AuthContext';
import { isFirebaseConfigured, db } from '../config/firebase';
import { doc, getDoc, setDoc, type Firestore } from 'firebase/firestore';
import { AppError } from '../services/firestore/errors';
import { util_log_debug_01 } from '../utils/logging';

const STORAGE_KEY = 'choppy:preferences';

interface PreferencesContextValue {
  preferences: UserPreferences;
  loading: boolean;
  updatePreferences: (prefs: Partial<UserPreferences>) => void;
  resetPreferences: () => void;
}

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

function loadLocal(): UserPreferences | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) } : null;
  } catch { return null; }
}

function saveLocal(prefs: UserPreferences) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs)); }
  catch { /* quota */ }
}

/** Simple Firestore read/write for preferences (single document, not a collection). */
function prefsDoc(database: Firestore, uid: string) {
  return doc(database, 'users', uid, 'profile', 'preferences');
}

export const PreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const toast = useToast();
  const [preferences, setPreferences] = useState<UserPreferences>(DEFAULT_PREFERENCES);
  const [loading, setLoading] = useState(true);
  const firestoreDown = useRef(false);
  const uid = user?.uid ?? null;

  // Load on mount + when user changes
  useEffect(() => {
    const load = async () => {
      setLoading(true);

      // Try Firestore first
      if (uid && isFirebaseConfigured() && db && !firestoreDown.current) {
        try {
          const snap = await getDoc(prefsDoc(db, uid));
          if (snap.exists()) {
            const data = snap.data() as Partial<UserPreferences>;
            const merged = { ...DEFAULT_PREFERENCES, ...data };
            setPreferences(merged);
            saveLocal(merged);
            util_log_debug_01('PreferencesContext: loaded from Firestore');
            setLoading(false);
            return;
          }
        } catch (err) {
          if (err instanceof AppError && err.code === 'SERVICE_UNAVAILABLE') {
            firestoreDown.current = true;
          }
          util_log_debug_01('PreferencesContext: Firestore unavailable, falling back');
        }
      }

      // Fall back to localStorage
      const local = loadLocal();
      if (local) {
        setPreferences(local);
        util_log_debug_01('PreferencesContext: loaded from localStorage');

        // Sync local → Firestore if signed in
        if (uid && isFirebaseConfigured() && db && !firestoreDown.current) {
          try {
            await setDoc(prefsDoc(db, uid), local, { merge: true });
            util_log_debug_01('PreferencesContext: synced localStorage → Firestore');
          } catch { /* best effort */ }
        }
      }

      setLoading(false);
    };

    load();
  }, [uid]);

  const updatePreferences = useCallback((partial: Partial<UserPreferences>) => {
    setPreferences(prev => {
      const merged = { ...prev, ...partial };
      saveLocal(merged);

      // Sync to Firestore in background
      if (uid && isFirebaseConfigured() && db && !firestoreDown.current) {
        setDoc(prefsDoc(db, uid), merged, { merge: true }).catch((err) => {
          if (err instanceof AppError && err.code === 'SERVICE_UNAVAILABLE') {
            firestoreDown.current = true;
            toast({
              title: 'Saved locally',
              description: 'Preferences saved on this device.',
              status: 'warning', duration: 4000, isClosable: true,
            });
          }
        });
      }

      return merged;
    });
  }, [uid, toast]);

  const resetPreferences = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
    saveLocal(DEFAULT_PREFERENCES);
  }, []);

  return (
    <PreferencesContext.Provider value={{ preferences, loading, updatePreferences, resetPreferences }}>
      {children}
    </PreferencesContext.Provider>
  );
};

export const usePreferences = (): PreferencesContextValue => {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences must be used inside <PreferencesProvider>');
  return ctx;
};
